// A tiny typed event emitter, shared by every client module (socket, connector, inputs, the
// client event bus). Moved here from net/socket.ts, which re-exports it unchanged.
//
// - `E` maps event names to payload types: `new Emitter<{ open: { url: string } }>()`.
// - Listeners run synchronously in subscription order. Subscribing or unsubscribing during an
//   emit takes effect from the next emit (copy-on-write listener lists), so `emit` itself never
//   allocates: safe for per-frame events.
// - A listener that throws: by default the error propagates out of `emit` (the remaining
//   listeners do not run), as before the move. With `onError` the error is handed to it and the
//   remaining listeners still run (the client event bus uses this so a failing audio or HUD
//   listener can never break the net handlers or the main loop).
//
// Implemented fully (contracts step). Consumers: net/socket.ts, net/discovery.ts, input/**,
// events/bus.ts.

export interface EmitterOptions {
  /** Called with a listener's exception (and the event name); the other listeners still run. */
  readonly onError?: (error: unknown, type: PropertyKey) => void;
}

type Listener = (ev: never) => void;

export class Emitter<E extends object> {
  private readonly listeners = new Map<keyof E, readonly Listener[]>();
  private readonly onError: ((error: unknown, type: PropertyKey) => void) | undefined;

  constructor(opts: EmitterOptions = {}) {
    this.onError = opts.onError;
  }

  /** Subscribes; returns the unsubscribe function (idempotent). */
  on<K extends keyof E>(type: K, fn: (ev: E[K]) => void): () => void {
    const l = fn as Listener;
    this.listeners.set(type, [...(this.listeners.get(type) ?? []), l]);
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const list = this.listeners.get(type);
      if (!list) return;
      const i = list.indexOf(l);
      if (i < 0) return;
      const next = list.slice(0, i).concat(list.slice(i + 1));
      if (next.length === 0) this.listeners.delete(type);
      else this.listeners.set(type, next);
    };
  }

  /** Subscribes for one emit only. */
  once<K extends keyof E>(type: K, fn: (ev: E[K]) => void): () => void {
    const off = this.on(type, (ev) => {
      off();
      fn(ev);
    });
    return off;
  }

  /** True when `type` has at least one listener (lets a hot path skip building a payload). */
  has<K extends keyof E>(type: K): boolean {
    return this.listeners.has(type);
  }

  emit<K extends keyof E>(type: K, ev: E[K]): void {
    const list = this.listeners.get(type);
    if (!list) return;
    const onError = this.onError;
    for (let i = 0; i < list.length; i++) {
      const fn = list[i] as (ev: E[K]) => void;
      if (onError === undefined) {
        fn(ev);
        continue;
      }
      try {
        fn(ev);
      } catch (err) {
        try {
          onError(err, type);
        } catch {
          // A failing error handler must not break the emit either.
        }
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
